import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon,
  DocumentIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { api } from '../services/api';

/**
 * Customer feedback triage panel on the super-admin dashboard.
 *
 * Purpose: stop feedback from slipping through the cracks. Renders the
 * unsolved-bad list first, lets a reviewer open the underlying PDF, and
 * ships a one-click "mark solved" so the queue actually drains.
 *
 * Backend routes: /admin/feedback, /admin/feedback/counts,
 * /admin/feedback/{id}/solve, /admin/feedback/{id}/unsolve.
 */

interface FeedbackAccount { id: number; slug: string; name: string; }
interface FeedbackUser { id: number; username: string; email?: string; }
interface FeedbackInvoice { id: number; filename: string | null; file_path: string | null; file_url: string | null; }

interface ThreadComment {
  id: number;
  body: string;
  is_from_admin: boolean;
  created_at: string | null;
  author: { id?: number; username: string } | null;
}

interface FeedbackItem {
  id: number;
  created_at: string | null;
  rating: string | null;
  subject: string | null;
  comment: string | null;
  solved: boolean;
  solved_at: string | null;
  solved_note: string | null;
  solved_by: FeedbackUser | null;
  reporter: FeedbackUser | null;
  account: FeedbackAccount | null;
  invoice: FeedbackInvoice | null;
  comments: ThreadComment[];
}

interface CountsResponse { total: number; unsolved: number; unsolved_bad: number; }

type SolvedFilter = 'unsolved' | 'solved' | 'all';
type RatingFilter = 'bad' | 'good' | 'all';

const fmtDate = (iso: string | null) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso: string | null) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${date} at ${time}`;
  } catch {
    return iso;
  }
};

const AdminFeedbackSection: React.FC = () => {
  const [counts, setCounts] = useState<CountsResponse | null>(null);
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [previewFor, setPreviewFor] = useState<number | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [solvedFilter, setSolvedFilter] = useState<SolvedFilter>('unsolved');
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>('all');
  const [accountFilter, setAccountFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [facetAccounts, setFacetAccounts] = useState<{ id: number; name: string; count: number }[]>([]);
  const [facetReporters, setFacetReporters] = useState<{ id: number; username: string; count: number }[]>([]);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  const [replyingFor, setReplyingFor] = useState<number | null>(null);
  const [replyMode, setReplyMode] = useState<'reply' | 'solve'>('reply');
  const [solveNote, setSolveNote] = useState('');
  const [expandedThreads, setExpandedThreads] = useState<Set<number>>(new Set());
  const toggleThread = (fbId: number) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(fbId)) next.delete(fbId);
      else next.add(fbId);
      return next;
    });
  };
  const [solveSubmitting, setSolveSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number | boolean> = { limit: 50, offset: 0 };
      if (solvedFilter !== 'all') params.solved = solvedFilter === 'solved';
      if (ratingFilter !== 'all') params.rating = ratingFilter;
      if (accountFilter) params.account_id = Number(accountFilter);
      if (userFilter) params.user_id = Number(userFilter);
      if (debouncedQ) params.q = debouncedQ;
      const facetParams: Record<string, string | number | boolean> = {};
      if (solvedFilter !== 'all') facetParams.solved = solvedFilter === 'solved';
      if (ratingFilter !== 'all') facetParams.rating = ratingFilter;
      if (debouncedQ) facetParams.q = debouncedQ;
      const [list, cnt, facets] = await Promise.all([
        api.get('/admin/feedback', { params }),
        api.get('/admin/feedback/counts'),
        api.get('/admin/feedback/facets', { params: facetParams }),
      ]);
      setItems(list.data?.items || []);
      setTotal(list.data?.total ?? 0);
      setCounts(cnt.data);
      setFacetAccounts(facets.data?.accounts || []);
      setFacetReporters(facets.data?.reporters || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [solvedFilter, ratingFilter, accountFilter, userFilter, debouncedQ]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openReplyFor = (fb: FeedbackItem, mode: 'reply' | 'solve') => {
    setReplyingFor(fb.id);
    setReplyMode(mode);
    setSolveNote('');
    // Auto-expand the thread when the admin is about to reply so they see
    // the prior conversation without having to click twice.
    if (fb.comments && fb.comments.length > 0) {
      setExpandedThreads((prev) => new Set(prev).add(fb.id));
    }
  };

  const cancelReply = () => {
    setReplyingFor(null);
    setSolveNote('');
  };

  const submitReply = async (fb: FeedbackItem) => {
    setSolveSubmitting(true);
    try {
      const trimmed = solveNote.trim();
      if (replyMode === 'solve') {
        await api.post(`/admin/feedback/${fb.id}/solve`, {
          note: trimmed || null,
        });
      } else {
        if (!trimmed) return;
        await api.post(`/admin/feedback/${fb.id}/comment`, { body: trimmed });
      }
      cancelReply();
      await fetchAll();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSolveSubmitting(false);
    }
  };

  const reopen = async (fb: FeedbackItem) => {
    try {
      await api.post(`/admin/feedback/${fb.id}/unsolve`);
      await fetchAll();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Reopen failed');
    }
  };

  // Fetch the PDF as a blob via the existing axios auth interceptor, then
  // open the object URL. We can't just point window.open at the /files/
  // route directly because the backend requires a Bearer header that a bare
  // window navigation can't send. Blob-URL is also what the inline iframe
  // preview uses, so we share the fetched blob across open + preview.
  const fetchPdfBlobUrl = useCallback(async (fb: FeedbackItem): Promise<string | null> => {
    if (!fb.invoice?.file_url) return null;
    try {
      const res = await api.get(fb.invoice.file_url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load PDF');
      return null;
    }
  }, []);

  const openPdf = async (fb: FeedbackItem) => {
    const url = await fetchPdfBlobUrl(fb);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const togglePreview = async (fb: FeedbackItem) => {
    if (previewFor === fb.id) {
      // collapsing
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
      setPreviewFor(null);
      return;
    }
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewFor(fb.id);
    setPreviewLoading(true);
    const url = await fetchPdfBlobUrl(fb);
    setPreviewBlobUrl(url);
    setPreviewLoading(false);
  };

  // Revoke the object URL on unmount so we don't leak memory when the
  // dashboard is closed with a preview still open.
  useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    };
  }, [previewBlobUrl]);

  const headerBadge = useMemo(() => {
    if (!counts) return null;
    const n = counts.unsolved_bad;
    if (n === 0) {
      return (
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300">
          0 to review
        </span>
      );
    }
    return (
      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300">
        {n} unsolved bad
      </span>
    );
  }, [counts]);

  const ratingPill = (r: string | null) => {
    const cls =
      r === 'bad'
        ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
        : r === 'good'
          ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
    return (
      <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${cls}`}>
        {r || 'none'}
      </span>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden mb-6">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 hover:bg-gray-100 dark:hover:bg-gray-700/60"
        aria-expanded={!collapsed}
      >
        <div className="flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Customer feedback</span>
          {headerBadge}
        </div>
        {collapsed ? <ChevronDownIcon className="h-4 w-4 text-gray-500" /> : <ChevronUpIcon className="h-4 w-4 text-gray-500" />}
      </button>

      {!collapsed && (
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search subject and comment..."
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <select
              value={solvedFilter}
              onChange={(e) => setSolvedFilter(e.target.value as SolvedFilter)}
              className="pl-3 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="unsolved">Unsolved</option>
              <option value="solved">Solved</option>
              <option value="all">All statuses</option>
            </select>
            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value as RatingFilter)}
              className="pl-3 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="bad">Bad only</option>
              <option value="good">Good only</option>
              <option value="all">All ratings</option>
            </select>
            <select
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              className="pl-3 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              title="Filter by account (county)"
            >
              <option value="">All accounts</option>
              {facetAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.count})
                </option>
              ))}
            </select>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="pl-3 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              title="Filter by reporter (user)"
            >
              <option value="">All users</option>
              {facetReporters.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username} ({u.count})
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={fetchAll}
              disabled={loading}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <div className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {items.length} of {total} shown
              {counts && (
                <span className="ml-2 text-gray-400 dark:text-gray-500">
                  ({counts.unsolved} unsolved, {counts.total} total)
                </span>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
            {items.length === 0 && !loading && (
              <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No feedback matches the current filters.
              </div>
            )}
            {items.map((fb) => {
              const isPreviewing = previewFor === fb.id && !!fb.invoice?.file_url;
              return (
                <div key={fb.id} className="px-4 py-3">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {ratingPill(fb.rating)}
                      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {fmtDate(fb.created_at)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {fb.subject || <em className="text-gray-500">(no subject)</em>}
                      </div>
                      {fb.comment && (
                        <div className="text-xs text-gray-700 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">
                          {fb.comment}
                        </div>
                      )}
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                        <span>{fb.account?.name || 'unknown account'}</span>
                        <span>&middot;</span>
                        <span>{fb.reporter?.username || 'anonymous'}</span>
                        {fb.invoice && (
                          <>
                            <span>&middot;</span>
                            <span>invoice #{fb.invoice.id}{fb.invoice.filename ? ` (${fb.invoice.filename})` : ''}</span>
                          </>
                        )}
                        {fb.solved && fb.solved_by && (
                          <>
                            <span>&middot;</span>
                            <span className="text-emerald-700 dark:text-emerald-300">
                              solved by {fb.solved_by.username}{fb.solved_at ? ` on ${fmtDate(fb.solved_at)}` : ''}
                            </span>
                          </>
                        )}
                        {fb.comments && fb.comments.length > 0 && (
                          <>
                            <span>&middot;</span>
                            <button
                              type="button"
                              onClick={() => toggleThread(fb.id)}
                              className="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-white"
                              title={expandedThreads.has(fb.id) ? 'Hide replies' : 'Show replies'}
                            >
                              <ChatBubbleLeftRightIcon className="h-3 w-3" />
                              {expandedThreads.has(fb.id) ? 'Hide' : 'Show'} {fb.comments.length} repl{fb.comments.length === 1 ? 'y' : 'ies'}
                              {fb.comments.some((c) => !c.is_from_admin) && (
                                <span className="ml-1 text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                                  reporter replied
                                </span>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {fb.invoice?.file_url && (
                        <>
                          <button
                            type="button"
                            onClick={() => togglePreview(fb)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                            title="Toggle inline PDF preview"
                          >
                            <DocumentIcon className="h-3.5 w-3.5" />
                            {isPreviewing ? 'Hide PDF' : 'Preview PDF'}
                          </button>
                          <button
                            type="button"
                            onClick={() => openPdf(fb)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                            title="Open PDF in a new tab"
                          >
                            Open PDF
                          </button>
                        </>
                      )}
                      {fb.solved ? (
                        <button
                          type="button"
                          onClick={() => reopen(fb)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                          Reopen
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => openReplyFor(fb, 'reply')}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-brand-300 dark:border-brand-700 text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/20 hover:bg-brand-100 dark:hover:bg-brand-900/40"
                            title="Reply to the reporter and keep this open"
                          >
                            <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                            Reply
                          </button>
                          <button
                            type="button"
                            onClick={() => openReplyFor(fb, 'solve')}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
                            title="Mark this as solved, optionally with a reply"
                          >
                            <CheckCircleIcon className="h-3.5 w-3.5" />
                            Mark solved
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {replyingFor === fb.id && (
                    <div
                      className={`mt-3 p-3 border rounded-md ${
                        replyMode === 'solve'
                          ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20'
                          : 'border-brand-300 dark:border-brand-700 bg-brand-50 dark:bg-brand-900/20'
                      }`}
                    >
                      <label
                        className={`block text-xs font-semibold mb-1 ${
                          replyMode === 'solve'
                            ? 'text-emerald-800 dark:text-emerald-300'
                            : 'text-brand-800 dark:text-brand-300'
                        }`}
                      >
                        {replyMode === 'solve'
                          ? 'Optional reply, sent when you mark this solved'
                          : 'Reply to the reporter (row stays Open)'}
                      </label>
                      <textarea
                        value={solveNote}
                        onChange={(e) => setSolveNote(e.target.value.slice(0, 2000))}
                        rows={3}
                        maxLength={2000}
                        placeholder={
                          replyMode === 'solve'
                            ? 'Short note the reporter will see in My Feedback (leave blank to solve silently)'
                            : 'Ask a question, share status, or acknowledge. The row stays Open.'
                        }
                        className={`w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent ${
                          replyMode === 'solve' ? 'focus:ring-emerald-500' : 'focus:ring-brand-500'
                        }`}
                      />
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 text-right mt-0.5">
                        {solveNote.length}/2000
                      </div>
                      <div className="flex justify-end gap-2 mt-2">
                        <button
                          type="button"
                          onClick={cancelReply}
                          disabled={solveSubmitting}
                          className="px-3 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => submitReply(fb)}
                          disabled={solveSubmitting || (replyMode === 'reply' && !solveNote.trim())}
                          className={`inline-flex items-center gap-1 px-3 py-1 text-xs rounded-md text-white disabled:opacity-50 ${
                            replyMode === 'solve'
                              ? 'bg-emerald-600 hover:bg-emerald-700'
                              : 'bg-brand-600 hover:bg-brand-700'
                          }`}
                        >
                          {replyMode === 'solve' ? (
                            <>
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                              {solveSubmitting ? 'Solving...' : 'Confirm solved'}
                            </>
                          ) : (
                            <>
                              <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                              {solveSubmitting ? 'Saving...' : 'Save reply'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {fb.comments && fb.comments.length > 0 && expandedThreads.has(fb.id) && (
                    <div className="mt-3 space-y-2">
                      {fb.comments.map((c) => (
                        <div
                          key={c.id}
                          className={`pl-3 border-l-2 rounded-r p-2 ${
                            c.is_from_admin
                              ? 'border-brand-400 dark:border-brand-600 bg-brand-50/60 dark:bg-brand-900/10'
                              : 'border-amber-400 dark:border-amber-600 bg-amber-50/60 dark:bg-amber-900/10'
                          }`}
                        >
                          <div
                            className={`text-[11px] font-semibold ${
                              c.is_from_admin
                                ? 'text-brand-800 dark:text-brand-300'
                                : 'text-amber-800 dark:text-amber-300'
                            }`}
                          >
                            {c.is_from_admin
                              ? `KeyScan support${c.author ? ` (${c.author.username})` : ''}`
                              : `${c.author?.username || 'Reporter'} replied`}
                            {c.created_at ? ` on ${fmtDateTime(c.created_at)}` : ''}
                          </div>
                          <div className="text-xs text-gray-800 dark:text-gray-200 mt-1 whitespace-pre-wrap break-words">
                            {c.body}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {isPreviewing && fb.invoice?.file_url && (
                    <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden bg-gray-50 dark:bg-gray-900">
                      {previewLoading && (
                        <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
                          Loading PDF...
                        </div>
                      )}
                      {!previewLoading && previewBlobUrl && (
                        <iframe
                          title={`Invoice ${fb.invoice.id} PDF`}
                          src={previewBlobUrl}
                          className="w-full"
                          style={{ height: 480 }}
                        />
                      )}
                      {!previewLoading && !previewBlobUrl && (
                        <div className="px-4 py-6 text-sm text-red-600 dark:text-red-400">
                          Could not load the PDF (see the error above).
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFeedbackSection;
